import TrustedHtml from '@components/common/TrustedHtml/TrustedHtml'
import ArrowLeftIcon from '@components/icons/ArrowLeftIcon/ArrowLeftIcon'
import ArrowRightIcon from '@components/icons/ArrowRightIcon/ArrowRightIcon'
import Container from '@components/ui/Container/Container'
import Media from '@components/ui/Media/Media'
import { UmbracoLink } from '@customTypes/umbracoLink'

import { MediaWithCropsFragment } from '@lib/umbraco/generated/types.generated'
import { isMobile, isTablet } from '@utils/helpers/DeviceUtils'
import clsx from 'clsx'
import { FC, useState } from 'react'
import { Keyboard, Navigation } from 'swiper/modules'
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'
import s from './CaseStories.module.scss'

type CaseStoriesProps = {
  caseTitle: string
  caseSubtitle: string
  caseLink: UmbracoLink
  caseContent: {
    image: MediaWithCropsFragment
    supportingText: string
  }[]
}

const CaseStories: FC<CaseStoriesProps> = ({
  caseTitle,
  caseSubtitle,
  caseLink,
  caseContent,
}) => {
  const [prevEl, setPrevEl] = useState<HTMLElement | null>(null)
  const [nextEl, setNextEl] = useState<HTMLElement | null>(null)

  const [activeIndex, setActiveIndex] = useState(0)

  // if first or last slide, fade out the prev/next buttons
  const onSlideChange = (e: SwiperClass) => {
    if (!nextEl || !prevEl) return

    setActiveIndex(e.activeIndex)

    if (e.isBeginning) {
      prevEl?.classList.add(s['hidden-btn'])
    } else {
      prevEl?.classList.remove(s['hidden-btn'])
    }
    if (e.isEnd) {
      nextEl?.classList.add(s['hidden-btn'])
    } else {
      nextEl?.classList.remove(s['hidden-btn'])
    }
  }

  return (
    <div className={s.root}>
      <TrustedHtml html={caseTitle} className={s.caseTitle} />
      <TrustedHtml html={caseSubtitle} className={s.caseSubtitle} />

      <Container className={s.container}>
        <div className={s.rootSlideContent}>
          <Swiper
            className={s.swiper}
            slidesPerView={isMobile() ? 1.5 : isTablet() ? 1.5 : 1}
            spaceBetween={50}
            grabCursor
            centeredSlides
            keyboard
            modules={[Keyboard, Navigation]}
            navigation={{ prevEl, nextEl }}
            enabled={caseContent?.length > 1}
            onSlideChange={onSlideChange}
          >
            <button
              className={clsx(s.prev, s['hidden-btn'])}
              ref={(node) => setPrevEl(node)}
            >
              <ArrowLeftIcon color={'black'} />
            </button>
            <button className={s.next} ref={(node) => setNextEl(node)}>
              <ArrowRightIcon color={'black'} />
            </button>

            {caseContent &&
              caseContent.map((caseItem, index) => (
                <SwiperSlide
                  key={index}
                  className="w-full h-full flex flex-col"
                >
                  <div className="w-full h-full flex flex-col justify-center md:justify-start">
                    <div className="md:flex-1 h-auto md:relative">
                      <div className="md:absolute md:inset-0">
                        <div className="w-full h-full flex justify-center items-center">
                          <Media
                            priority
                            className={s.mediaHeightUnset}
                            mediaClassName={s.caseImage}
                            mediaWithCrops={caseItem.image}
                          />
                        </div>
                      </div>
                    </div>

                    <TrustedHtml
                      html={caseItem.supportingText}
                      className={clsx(s.caseText, {
                        [s.active]: activeIndex === index,
                      })}
                    />
                  </div>
                </SwiperSlide>
              ))}
          </Swiper>
        </div>
      </Container>
    </div>
  )
}

export default CaseStories
